/* eslint-disable no-empty-pattern */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

export const getBusQustions = createAsyncThunk(
  "questionnaire/getBusQustions",
  async ({ orgId, status, dealCategoryId }, { rejectWithValue }) => {
    const statusArr = Array.isArray(status) ? status : [status];
    if (status === "PENDING") {
      statusArr.push("REJECTED");
    }
    try {
      const response = await axios.get(
        `/api/admin/questionnaire-thread/organization/${orgId}?status=${statusArr.join(",")}&dealCategoryId=${dealCategoryId}`,
      );
      return Array.isArray(response?.response) ? response.response : [];
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const submitBusQustions = createAsyncThunk(
  "questionnaire/submitBusQustions",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/admin/questionnaire-thread/answer`,
        payload,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getOnboardingStepStatus = createAsyncThunk(
  "questionnaire/getOnboardingStepStatus",
  async ({ orgId, dealCategoryId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/admin/getBusinessStatus/${orgId}?dealCategoryId=${dealCategoryId}`,
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const verifyBusPan = createAsyncThunk(
  "questionnaire/verifyBusPan",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/business/verifyBusPan`, payload);
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const verifyBusGstin = createAsyncThunk(
  "questionnaire/verifyBusGstin",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/business/verifyBusGstin`,
        payload,
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const verifyIfsc = createAsyncThunk(
  "questionnaire/verifyIfsc",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/business/busVerifyIfsc`, payload);
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const verifyBank = createAsyncThunk(
  "questionnaire/verifyBank",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/business/verifyBank`, payload);
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const getConsentDigilocker = createAsyncThunk(
  "questionnaire/getConsentDigilocker",
  async ({ orgId, dealCategoryId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/business/getConsentDigilocker?orgId=${orgId}&dealCategoryId=${dealCategoryId}`,
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const getAadhaarAndPan = createAsyncThunk(
  "questionnaire/getAadhaarAndPan",
  async ({ orgId, dealCategoryId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/business/getAadhaarAndPan?orgId=${orgId}&dealCategoryId=${dealCategoryId}`,
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const getKycDetails = createAsyncThunk(
  "questionnaire/getKycDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/business/getKycDetails`,
      );
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const getCompleteKycLink = createAsyncThunk(
  "questionnaire/getCompleteKycLink",
  async (dealCategoryId, { rejectWithValue }) => {
    try {
      const dealCategoryQuery = dealCategoryId
        ? `?dealCategoryId=${dealCategoryId}`
        : "";
      const response = await axios.get(
        `/api/business/getCompleteKycLink${dealCategoryQuery}`,
      );
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "Failed to open Complete KYC");
    }
  },
);

export const QuestionnaireSlice = createSlice({
  name: "questionnaire",
  initialState: {
    questionsData: [],
    loading: false,
    error: null,
    submitLoading: false,
    submitError: null,
    onboardingStepStatus: null,
    onboardingStepStatusLoad: "idle",
    onboardingDealCategoryId: null,
    panData: {},
    panError: "",
    panLoading: false,
    gstinLoading: false,
    gstinError: "",
    ifscLoading: false,
    ifscData: {},
    bankLoading: false,
    digilockerLoading: false,
    aadhaarPanLoading: false,
    aadhaarPanError: "",
    kycDetails: {},
    kycLoading: false,
    kycError: "",
  },

  extraReducers: (builder) => {
    builder.addCase(getBusQustions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getBusQustions.fulfilled, (state, action) => {
      state.loading = false;
      state.questionsData = Array.isArray(action.payload) ? action.payload : [];
      state.error = null;
    });
    builder.addCase(getBusQustions.rejected, (state, action) => {
      state.loading = false;
      state.questionsData = [];
      state.error = action.payload;
    });
    builder.addCase(submitBusQustions.pending, (state) => {
      state.submitLoading = true;
      state.submitError = null;
    });
    builder.addCase(submitBusQustions.fulfilled, (state) => {
      state.submitLoading = false;
      state.submitError = null;
    });
    builder.addCase(submitBusQustions.rejected, (state, action) => {
      state.submitLoading = false;
      state.submitError = action.payload;
    });

    builder.addCase(getOnboardingStepStatus.pending, (state, action) => {
      state.loading = true;
      state.onboardingStepStatusLoad = "loading";
      state.error = null;
      const nextId = action.meta?.arg?.dealCategoryId;
      if (nextId && state.onboardingDealCategoryId !== nextId) {
        state.onboardingStepStatus = null;
        state.onboardingDealCategoryId = null;
      }
    });
    builder.addCase(getOnboardingStepStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.onboardingStepStatusLoad = "succeeded";
      state.onboardingStepStatus = action.payload;
      state.onboardingDealCategoryId = action.meta?.arg?.dealCategoryId ?? null;
      state.error = null;
    });
    builder.addCase(getOnboardingStepStatus.rejected, (state, action) => {
      state.loading = false;
      state.onboardingStepStatusLoad = "failed";
      state.onboardingStepStatus = null;
      state.onboardingDealCategoryId = action.meta?.arg?.dealCategoryId ?? null;
      state.error = action.payload;
    });

    builder.addCase(verifyBusPan.pending, (state) => {
      state.panLoading = true;
      state.panError = "";
    });
    builder.addCase(verifyBusPan.fulfilled, (state, action) => {
      state.panLoading = false;
      state.panData = action.payload;
    });
    builder.addCase(verifyBusPan.rejected, (state, action) => {
      state.panLoading = false;
      state.panData = {};
      state.panError = action.payload;
    });

    builder.addCase(verifyBusGstin.pending, (state) => {
      state.gstinLoading = true;
      state.gstinError = "";
    });
    builder.addCase(verifyBusGstin.fulfilled, (state, action) => {
      state.gstinLoading = false;
      state.gstinError = "";
    });
    builder.addCase(verifyBusGstin.rejected, (state, action) => {
      state.gstinLoading = false;
      state.gstinError = action.payload;
    });

    builder.addCase(verifyIfsc.pending, (state) => {
      state.ifscLoading = true;
      state.error = "";
    });
    builder.addCase(verifyIfsc.fulfilled, (state, action) => {
      state.ifscLoading = false;
      state.ifscData = action.payload.response;
    });
    builder.addCase(verifyIfsc.rejected, (state, action) => {
      state.ifscLoading = false;
      state.ifscData = {};
      state.error = action.payload;
    });

    builder.addCase(verifyBank.pending, (state) => {
      state.bankLoading = true;
      state.error = "";
    });
    builder.addCase(verifyBank.fulfilled, (state, action) => {
      state.bankLoading = false;
      state.error = "";
    });
    builder.addCase(verifyBank.rejected, (state, action) => {
      state.bankLoading = false;
      state.error = action.payload;
    });

    builder.addCase(getConsentDigilocker.pending, (state) => {
      state.digilockerLoading = true;
      state.error = "";
    });
    builder.addCase(getConsentDigilocker.fulfilled, (state, action) => {
      state.digilockerLoading = false;
      state.error = "";
    });
    builder.addCase(getConsentDigilocker.rejected, (state, action) => {
      state.digilockerLoading = false;
      state.error = action.payload;
    });

    builder.addCase(getAadhaarAndPan.pending, (state) => {
      state.aadhaarPanLoading = true;
      state.aadhaarPanError = "";
    });
    builder.addCase(getAadhaarAndPan.fulfilled, (state, action) => {
      state.aadhaarPanLoading = false;
      state.aadhaarPanError = "";
    });
    builder.addCase(getAadhaarAndPan.rejected, (state, action) => {
      state.aadhaarPanLoading = false;
      state.aadhaarPanError = action.payload;
    });

    builder.addCase(getKycDetails.pending, (state) => {
      state.kycLoading = true;
      state.kycError = "";
    });
    builder.addCase(getKycDetails.fulfilled, (state, action) => {
      state.kycLoading = false;
      state.kycDetails = action.payload;
      state.kycError = "";
    });
    builder.addCase(getKycDetails.rejected, (state, action) => {
      state.kycLoading = false;
      state.kycError = action.payload;
    });
  },
  reducers: {
    resetQuestions: (state) => {
      state.questionsData = [];
    },
    resetOnboardingStatus: (state) => {
      state.onboardingStepStatus = null;
      state.onboardingStepStatusLoad = "idle";
      state.onboardingDealCategoryId = null;
    },
    clearQuestionnaireErrors: (state) => {
      state.error = null;
      state.submitError = null;
      state.panError = "";
      state.gstinError = "";
      state.aadhaarPanError = "";
      state.kycError = "";
    },
  },
});

export const { resetQuestions, resetOnboardingStatus, clearQuestionnaireErrors } =
  QuestionnaireSlice.actions;

export default QuestionnaireSlice.reducer;
